import React, { useState, useEffect, useCallback, useRef } from 'react';
import Editor from '@monaco-editor/react';
import FileTree from './FileTree';
import { useProjectStore } from '../../stores/projectStore';
import { projectApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Same as projectStore — local builder uses REACT_APP_DEPLOYMENT_MODE=local in .env
const DEPLOYMENT_MODE = process.env.REACT_APP_DEPLOYMENT_MODE || 'local';

// Image file extensions
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp'];

const isImageFile = (filePath) => {
  const ext = filePath?.split('.').pop()?.toLowerCase();
  return IMAGE_EXTENSIONS.includes(ext);
};

function collectFileMeta(nodes, out = {}) {
  for (const node of nodes || []) {
    if (node.type === 'file') {
      out[node.path] = `${node.modified || ''}|${node.size ?? ''}`;
    }
    if (node.children) collectFileMeta(node.children, out);
  }
  return out;
}

/**
 * CodeEditorPanel - Full code editor with file tree, tabs, and Monaco editor
 */
function CodeEditorPanel({ active = false }) {
  const { currentProject, files, loadFiles, readFile, writeFile, setShowHiddenFiles } = useProjectStore();
  const { isSuperAdmin } = useAuth();
  
  // Editor state
  const [openTabs, setOpenTabs] = useState([]); // { path, content, originalContent, isModified }
  const [activeTab, setActiveTab] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error' | null
  const [revealPaths, setRevealPaths] = useState([]);
  
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const saveRef = useRef(null);
  const openTabsRef = useRef(openTabs);
  const fileMetaRef = useRef({});
  openTabsRef.current = openTabs;

  // Superadmins always; local deployment (REACT_APP_DEPLOYMENT_MODE=local) shows .threedium in tree
  useEffect(() => {
    setShowHiddenFiles(!!isSuperAdmin || DEPLOYMENT_MODE === 'local');
  }, [isSuperAdmin, setShowHiddenFiles]);

  const reloadUnmodifiedTabs = useCallback(async (paths) => {
    const tabs = openTabsRef.current;
    if (!tabs.length) return;
    const next = await Promise.all(tabs.map(async (tab) => {
      if (tab.isModified || tab.isImage) return tab;
      if (paths && !paths.has(tab.path)) return tab;
      try {
        const content = await readFile(tab.path);
        if (content === tab.content) return tab;
        return { ...tab, content: content || '', originalContent: content || '', isModified: false };
      } catch {
        return tab;
      }
    }));
    setOpenTabs(next);
  }, [readFile]);

  const revealNewFiles = useCallback((meta, prev) => {
    const added = Object.keys(meta).filter((path) => !(path in prev));
    if (Object.keys(prev).length) {
      if (added.length) setRevealPaths(added);
      return;
    }
    const publicFiles = Object.keys(meta).filter((path) => path.startsWith('public/'));
    if (publicFiles.length) setRevealPaths(publicFiles);
  }, []);

  // Refresh tree + unmodified buffers each time the Code pill is opened
  useEffect(() => {
    if (!active || !currentProject?.id) return;
    let cancelled = false;
    (async () => {
      await loadFiles(true);
      if (cancelled) return;
      const meta = collectFileMeta(useProjectStore.getState().files);
      revealNewFiles(meta, fileMetaRef.current);
      fileMetaRef.current = meta;
      await reloadUnmodifiedTabs();
    })();
    return () => { cancelled = true; };
  }, [active, currentProject?.id, loadFiles, reloadUnmodifiedTabs, revealNewFiles]);

  // Keep meta while Preview is open; while Code is visible, reveal writes and reload changed tabs
  useEffect(() => {
    const meta = collectFileMeta(files);
    const prev = fileMetaRef.current;
    if (active) {
      const changed = new Set();
      for (const [path, sig] of Object.entries(meta)) {
        if (prev[path] && prev[path] !== sig) changed.add(path);
      }
      revealNewFiles(meta, prev);
      if (changed.size) reloadUnmodifiedTabs(changed);
    }
    fileMetaRef.current = meta;
  }, [files, active, reloadUnmodifiedTabs, revealNewFiles]);

  // Reset Monaco undo stack when switching tabs so undo doesn't bleed across files
  useEffect(() => {
    if (editorRef.current && activeTab) {
      const model = editorRef.current.getModel();
      if (model) {
        model.pushStackElement();
      }
    }
  }, [activeTab]);

  // Get language from file extension
  const getLanguage = (filePath) => {
    const ext = filePath?.split('.').pop()?.toLowerCase();
    const languageMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'css': 'css',
      'scss': 'scss',
      'html': 'html',
      'json': 'json',
      'md': 'markdown',
      'py': 'python',
      'yaml': 'yaml',
      'yml': 'yaml',
      'xml': 'xml',
      'svg': 'xml',
    };
    return languageMap[ext] || 'plaintext';
  };

  // Handle file selection from tree
  const handleFileSelect = useCallback(async (filePath) => {
    // Check if already open
    const existingTab = openTabs.find(tab => tab.path === filePath);
    if (existingTab) {
      setActiveTab(filePath);
      return;
    }

    // Handle image files differently
    if (isImageFile(filePath)) {
      const imageUrl = projectApi.getRawFileUrl(currentProject.id, filePath);
      const newTab = {
        path: filePath,
        content: null,
        originalContent: null,
        isModified: false,
        isImage: true,
        imageUrl: imageUrl
      };
      setOpenTabs(prev => [...prev, newTab]);
      setActiveTab(filePath);
      return;
    }

    // Load text file content
    setIsLoading(true);
    try {
      const content = await readFile(filePath);
      const newTab = {
        path: filePath,
        content: content || '',
        originalContent: content || '',
        isModified: false,
        isImage: false
      };
      setOpenTabs(prev => [...prev, newTab]);
      setActiveTab(filePath);
    } catch (error) {
      console.error('Failed to load file:', error);
    } finally {
      setIsLoading(false);
    }
  }, [openTabs, readFile, currentProject]);

  // Handle editor content change
  const handleEditorChange = useCallback((value) => {
    setOpenTabs(prev => prev.map(tab => {
      if (tab.path === activeTab) {
        const isModified = value !== tab.originalContent;
        return { ...tab, content: value, isModified };
      }
      return tab;
    }));
  }, [activeTab]);

  // Save current file
  const saveCurrentFile = useCallback(async () => {
    const currentTab = openTabs.find(tab => tab.path === activeTab);
    if (!currentTab || !currentTab.isModified) return;

    setSaveStatus('saving');
    try {
      await writeFile(currentTab.path, currentTab.content);
      
      // Update tab state
      setOpenTabs(prev => prev.map(tab => {
        if (tab.path === activeTab) {
          return { ...tab, originalContent: tab.content, isModified: false };
        }
        return tab;
      }));
      
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
      
      // Refresh files list
      loadFiles();
    } catch (error) {
      console.error('Failed to save file:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  }, [activeTab, openTabs, writeFile, loadFiles]);

  // Keep ref in sync so Monaco's addCommand always calls the latest version
  saveRef.current = saveCurrentFile;

  // Close tab (with unsaved-changes guard)
  const closeTab = useCallback((path, e) => {
    e?.stopPropagation();

    const target = openTabs.find(tab => tab.path === path);
    if (target?.isModified && !window.confirm('This file has unsaved changes. Close anyway?')) {
      return;
    }
    
    const tabIndex = openTabs.findIndex(tab => tab.path === path);
    const newTabs = openTabs.filter(tab => tab.path !== path);
    setOpenTabs(newTabs);
    
    // If closing active tab, switch to adjacent tab
    if (activeTab === path && newTabs.length > 0) {
      const newActiveIndex = Math.min(tabIndex, newTabs.length - 1);
      setActiveTab(newTabs[newActiveIndex].path);
    } else if (newTabs.length === 0) {
      setActiveTab(null);
    }
  }, [openTabs, activeTab]);

  // Handle keyboard shortcuts (stable ref — no listener churn on tab switch)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveRef.current?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Editor mount handler
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      saveRef.current?.();
    });
  };

  const currentTab = openTabs.find(tab => tab.path === activeTab);
  const hasUnsavedChanges = openTabs.some(tab => tab.isModified);

  return (
    <div className="code-editor-panel">
      {/* File Tree Sidebar */}
      <div className="code-editor-sidebar">
        <div className="code-editor-sidebar-header">
          <span className="sidebar-title">Files</span>
          {hasUnsavedChanges && (
            <span className="unsaved-indicator" title="Unsaved changes">●</span>
          )}
        </div>
        <FileTree
          files={files}
          onFileSelect={handleFileSelect}
          selectedFile={activeTab}
          projectId={currentProject?.id}
          revealPaths={revealPaths}
        />
      </div>

      {/* Editor Area */}
      <div className="code-editor-main">
        {/* Tabs */}
        <div className="code-editor-tabs">
          <div className="tabs-list">
            {openTabs.map(tab => (
              <div
                key={tab.path}
                className={`editor-tab ${activeTab === tab.path ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.path)}
              >
                <span className="tab-name">
                  {tab.isModified && <span className="tab-modified">●</span>}
                  {tab.path.split('/').pop()}
                </span>
                <button
                  className="tab-close"
                  onClick={(e) => closeTab(tab.path, e)}
                  title="Close"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          
          {/* Save Button */}
          {currentTab && (
            <div className="editor-actions">
              {saveStatus && (
                <span className={`save-status ${saveStatus}`}>
                  {saveStatus === 'saving' && 'Saving...'}
                  {saveStatus === 'saved' && '✓ Saved'}
                  {saveStatus === 'error' && '✗ Error'}
                </span>
              )}
              <button
                className={`save-btn ${currentTab.isModified ? 'active' : ''}`}
                onClick={saveCurrentFile}
                disabled={!currentTab.isModified}
                title="Save (Ctrl+S)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                Save
              </button>
            </div>
          )}
        </div>

        {/* Monaco Editor */}
        <div className="code-editor-content">
          {isLoading ? (
            <div className="editor-loading">
              <div className="loading-spinner"></div>
              <span>Loading file...</span>
            </div>
          ) : currentTab?.isImage ? (
            <div className="image-preview-container">
              <img 
                src={currentTab.imageUrl} 
                alt={currentTab.path.split('/').pop()}
                className="image-preview"
              />
              <div className="image-preview-info">
                <span className="image-filename">{currentTab.path.split('/').pop()}</span>
                <span className="image-path">{currentTab.path}</span>
              </div>
            </div>
          ) : currentTab ? (
            <Editor
              height="100%"
              language={getLanguage(currentTab.path)}
              value={currentTab.content}
              onChange={handleEditorChange}
              onMount={handleEditorDidMount}
              theme="vs-dark"
              options={{
                fontSize: 13,
                fontFamily: "'Fira Code', 'SF Mono', Menlo, monospace",
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                lineNumbers: 'on',
                renderLineHighlight: 'all',
                cursorBlinking: 'smooth',
                smoothScrolling: true,
                padding: { top: 16, bottom: 16 },
                automaticLayout: true,
              }}
            />
          ) : (
            <div className="editor-placeholder">
              <div className="placeholder-content">
                <span className="placeholder-icon">📝</span>
                <h3>No file open</h3>
                <p>Select a file from the tree to edit</p>
                <div className="placeholder-shortcuts">
                  <kbd>Ctrl</kbd> + <kbd>S</kbd> to save
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CodeEditorPanel;



